'use client';

import { useEffect, useRef, useState } from 'react';

export type AudioMonitoringResult = {
	mics: MediaDeviceInfo[];
	volumeLevel: number;
	bandLevels: [number, number, number];
	micError: string | null;
};

type WindowWithWebkitAudio = typeof window & {
	webkitAudioContext?: typeof AudioContext;
};

export function useAudioMonitoring(
	active: boolean,
	deviceId: string | null,
): AudioMonitoringResult {
	const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
	const [volumeLevel, setVolumeLevel] = useState(0);
	const [bandLevels, setBandLevels] = useState<[number, number, number]>([
		0, 0, 0,
	]);
	const [micError, setMicError] = useState<string | null>(null);

	const streamRef = useRef<MediaStream | null>(null);
	const contextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		let cancelled = false;

		const stop = () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((t) => t.stop());
				streamRef.current = null;
			}
			analyserRef.current = null;
			setVolumeLevel(0);
			setBandLevels([0, 0, 0]);
		};

		if (!active) {
			stop();
			return;
		}

		const start = async () => {
			stop();
			setMicError(null);
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: deviceId ? { deviceId: { exact: deviceId } } : true,
				});
				if (cancelled) {
					stream.getTracks().forEach((t) => t.stop());
					return;
				}
				streamRef.current = stream;
				const w = window as WindowWithWebkitAudio;
				const Ctx = window.AudioContext || w.webkitAudioContext;
				if (!Ctx) {
					setMicError('AudioContext не поддерживается');
					return;
				}
				if (!contextRef.current || contextRef.current.state === 'closed') {
					contextRef.current = new Ctx();
				}
				if (contextRef.current.state === 'suspended') {
					await contextRef.current.resume();
				}
				const source = contextRef.current.createMediaStreamSource(stream);
				const analyser = contextRef.current.createAnalyser();
				analyser.fftSize = 512;
				analyser.smoothingTimeConstant = 0.15;
				source.connect(analyser);
				analyserRef.current = analyser;

				const time = new Uint8Array(analyser.fftSize);
				const freq = new Uint8Array(analyser.frequencyBinCount);

				const tick = () => {
					if (!analyserRef.current) return;
					analyserRef.current.getByteTimeDomainData(time);
					let sumSq = 0;
					for (const v of time) {
						const n = (v - 128) / 128;
						sumSq += n * n;
					}
					setVolumeLevel(Math.min(100, Math.sqrt(sumSq / time.length) * 320));
					analyserRef.current.getByteFrequencyData(freq);
					const useful = Math.min(96, freq.length);
					const bandSize = Math.floor(useful / 3);
					const bands = [0, 1, 2].map((b) => {
						let sum = 0;
						for (let i = b * bandSize; i < (b + 1) * bandSize; i++) {
							sum += freq[i];
						}
						return Math.min(100, (sum / bandSize / 255) * 200);
					}) as [number, number, number];
					setBandLevels(bands);
					rafRef.current = requestAnimationFrame(tick);
				};
				tick();

				const devices = await navigator.mediaDevices.enumerateDevices();
				if (!cancelled) {
					setMics(devices.filter((d) => d.kind === 'audioinput'));
				}
			} catch (e: unknown) {
				const name = (e as { name?: string })?.name;
				setMicError(
					name === 'NotAllowedError'
						? 'Доступ к микрофону запрещён'
						: 'Не удалось получить доступ к микрофону',
				);
			}
		};

		start();
		return () => {
			cancelled = true;
			stop();
		};
	}, [active, deviceId]);

	return { mics, volumeLevel, bandLevels, micError };
}
